import { useEffect } from 'react';
import { Organization } from './useOrganizations';

interface MetaTagsOptions {
  title?: string;
  description?: string;
  organization?: Organization | null;
}

const DEFAULT_DESCRIPTION = 'Football Competitions for Your Organization';

export const useMetaTags = ({ title, description, organization }: MetaTagsOptions) => {
  useEffect(() => {
    console.log('[useMetaTags] Hook triggered with:', {
      title,
      description,
      organizationName: organization?.name,
      organizationMetaDesc: organization?.meta_description,
      organizationDesc: organization?.description
    });

    // Set description priority: custom > organization meta_description > organization description > default
    const finalDescription =
      description ||
      organization?.meta_description ||
      organization?.description ||
      DEFAULT_DESCRIPTION;

    // Determine the title to set
    let newTitle: string;
    if (title) {
      newTitle = title;
      console.log('[useMetaTags] Set title (custom):', title);
    } else if (organization?.name) {
      // For organizations, just use the organization name as the title
      newTitle = organization.name;
      console.log('[useMetaTags] Set title (organization):', newTitle);
    } else {
      newTitle = 'Footy Picks';
      console.log('[useMetaTags] Set title (default):', newTitle);
    }

    // Update page title using multiple methods to ensure Safari compatibility
    // Method 1: Set document.title (standard approach)
    document.title = newTitle;

    // Method 2: Also update the <title> element directly (Safari workaround)
    let titleElement = document.querySelector('title');
    if (!titleElement) {
      titleElement = document.createElement('title');
      document.head.appendChild(titleElement);
    }
    titleElement.textContent = newTitle;

    // Force title update with a slight delay to ensure it sticks
    // This handles cases where React's render cycle might interfere
    const timeoutId = setTimeout(() => {
      document.title = newTitle;
      if (titleElement) {
        titleElement.textContent = newTitle;
      }
      console.log('[useMetaTags] Force-set title after delay:', newTitle);
    }, 10);

    // Update or create meta description tag
    let metaDescription = document.querySelector('meta[name="description"]');

    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute('content', finalDescription);

    // Update apple-mobile-web-app-title to match the page title
    // Safari sometimes uses this for tab titles instead of document.title
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement;
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitle);
    }
    appleTitle.content = newTitle;

    console.log('[useMetaTags] Final state:', {
      documentTitle: document.title,
      titleElementText: titleElement?.textContent,
      metaDescriptionContent: metaDescription.getAttribute('content'),
      appleTitleContent: appleTitle.content
    });

    // Delay check to see if title is being overwritten
    setTimeout(() => {
      console.log('[useMetaTags] Title after 100ms:', document.title);
    }, 100);

    // Cleanup function to clear the timeout
    return () => {
      clearTimeout(timeoutId);
    };
  }, [title, description, organization]);
};
